/**
 * @module FieldControls
 */
import { Avatar, Chip, createStyles, FormControl, FormGroup, FormHelperText, Input, InputLabel, makeStyles, MenuItem, Select, Typography } from '@material-ui/core';
import { deepMerge, PathHelper } from '@sensenet/client-utils';
import React, { useCallback, useEffect, useState } from 'react';
import { renderIconDefault } from "./icon.js";
import { defaultLocalization } from "./localization.js";
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const menuProps = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  getContentAnchorEl: null,
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};
const useStyles = makeStyles(() => createStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  select: {
    paddingBottom: 4,
    '&:focus': {
      backgroundColor: 'transparent'
    }
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap'
  },
  chip: {
    margin: '0 3px 3px 0'
  }
}));

/**
 * Field control that represents a Reference field. Available values will be populated from the FieldSettings.
 */
export const TagsInput = props => {
  var _props$localization;
  const localization = deepMerge(defaultLocalization.tagsInput, (_props$localization = props.localization) === null || _props$localization === void 0 ? void 0 : _props$localization.tagsInput);
  const [fieldValue, setFieldValue] = useState([]);
  const [dataSource, setDataSource] = useState([]);
  const classes = useStyles(props);
  const handleChange = event => {
    var _props$fieldOnChange;
    let s = [];
    props.settings.AllowMultiple && Array.isArray(event.target.value) ? s = event.target.value.reduce((value, c) => {
      const content = getContentById(c);
      if (content) {
        value.push(content);
      }
      return value;
    }, []) : s = [getContentById(event.target.value)];
    setFieldValue(s);
    (_props$fieldOnChange = props.fieldOnChange) === null || _props$fieldOnChange === void 0 ? void 0 : _props$fieldOnChange.call(props, props.settings.Name, s.reduce((contentIds, content) => {
      if (content) {
        contentIds.push(content.Id);
      }
      return contentIds;
    }, []));
  };
  const getDefaultValue = useCallback(async () => {
    if (!props.settings.DefaultValue || !props.repository) {
      return;
    }
    const defaultValue = props.settings.DefaultValue.split(/,|;/).filter(value => {
      var _props$settings$Selec;
      return (_props$settings$Selec = props.settings.SelectionRoots) !== null && _props$settings$Selec !== void 0 && _props$settings$Selec.length ? props.settings.SelectionRoots.some(root => PathHelper.isInSubTree(value, root)) : true;
    }).slice(0, props.settings.AllowMultiple ? undefined : 1);
    try {
      const responses = await Promise.all(defaultValue.map(async contentPath => await props.repository.load({
        idOrPath: contentPath,
        oDataOptions: {
          select: 'all'
        }
      })));
      const defaultContent = responses.map(response => response.d);
      setFieldValue(defaultContent);
    } catch (error) {
      console.error('At least one request for default reference value has failed.');
    }
  }, [props.repository, props.settings.DefaultValue, props.settings.SelectionRoots, props.settings.AllowMultiple]);
  const getSelected = useCallback(async () => {
    try {
      if (!props.repository) {
        throw new Error('You must pass a repository to this control');
      }
      const loadPath = props.content ? PathHelper.joinPaths('/', PathHelper.getContentUrl(props.content.Path), props.settings.Name) : '';
      const references = await props.repository.load({
        idOrPath: loadPath,
        oDataOptions: {
          select: 'all'
        }
      });
      let result = [references.d];
      if (Object.prototype.hasOwnProperty.call(references.d, 'results')) {
        result = references.d.results;
      }
      setFieldValue(result);
      return result;
    } catch (error) {
      console.error(error.message);
    }
  }, [props.content, props.repository, props.settings.Name]);
  const search = useCallback(async () => {
    try {
      if (!props.repository) {
        throw new Error('You must pass a repository to this control');
      }
      const selectionRoot = props.settings.SelectionRoots || [];
      const allowedTypes = props.settings.AllowedTypes || ['GenericContent'];
      let pathQuery = '';
      selectionRoot.forEach((selectionPath, index) => {
        pathQuery += index === 0 ? `InTree:${selectionPath}` : ` OR InTree:${selectionPath}`;
      });
      let typeQuery = '';
      allowedTypes.forEach((type, index) => {
        typeQuery += index === 0 ? `TypeIs:${type}` : ` OR TypeIs:${type}`;
      });
      const req = await props.repository.loadCollection({
        path: '/Root',
        oDataOptions: {
          query: `${pathQuery && `(${pathQuery})`}${pathQuery && typeQuery ? ' AND ' : ''}${typeQuery}`,
          select: 'all'
        }
      });
      setDataSource(req.d.results);
      if (props.actionName !== 'new') {
        getSelected();
      }
      return req;
    } catch (error) {
      console.error(error.mesage);
    }
  }, [getSelected, props.actionName, props.repository, props.settings.AllowedTypes, props.settings.SelectionRoots]);

  /**
   * returns a content by its id
   */
  const getContentById = id => {
    return dataSource.find(item => item.Id === id);
  };
  const handleDelete = id => {
    var _props$fieldOnChange2;
    const newValue = fieldValue.filter(item => item.Id !== id);
    setFieldValue(newValue);
    (_props$fieldOnChange2 = props.fieldOnChange) === null || _props$fieldOnChange2 === void 0 ? void 0 : _props$fieldOnChange2.call(props, props.settings.Name, newValue.map(content => content.Id));
  };

  /**
   * Get proper value for the Select component
   */
  const getValue = () => {
    if (props.settings.AllowMultiple) {
      return fieldValue.length ? fieldValue.map(c => c.Id) : [];
    }
    return fieldValue !== null && fieldValue !== void 0 && fieldValue.length ? fieldValue[0].Id : '';
  };
  useEffect(() => {
    if (props.actionName === 'browse') {
      getSelected();
    }
  }, [getSelected, props.actionName]);
  useEffect(() => {
    if (props.actionName === 'new') {
      getDefaultValue();
    }
  }, [props.actionName, getDefaultValue]);
  useEffect(() => {
    if (props.actionName !== 'browse') {
      search();
    }
  }, [props.actionName, search]);
  switch (props.actionName) {
    case 'edit':
    case 'new':
      return /*#__PURE__*/React.createElement(FormControl, {
        className: classes.root,
        key: props.settings.Name,
        component: 'fieldset',
        required: props.settings.Compulsory
      }, /*#__PURE__*/React.createElement(InputLabel, {
        required: props.settings.Compulsory,
        htmlFor: props.settings.Name
      }, props.settings.DisplayName), /*#__PURE__*/React.createElement(Select, {
        value: getValue(),
        onChange: handleChange,
        classes: {
          select: classes.select
        },
        multiple: props.settings.AllowMultiple,
        input: /*#__PURE__*/React.createElement(Input, {
          id: props.settings.Name,
          fullWidth: true
        }),
        renderValue: () => /*#__PURE__*/React.createElement("div", {
          className: classes.chips
        }, fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.map(content => {
          var _props$repository, _content$Avatar, _content$DisplayName, _props$repository2;
          return /*#__PURE__*/React.createElement(Chip, {
            avatar: (_props$repository = props.repository) !== null && _props$repository !== void 0 && _props$repository.schemas.isContentFromType(content, 'User') ? (_content$Avatar = content.Avatar) !== null && _content$Avatar !== void 0 && _content$Avatar.Url ? /*#__PURE__*/React.createElement(Avatar, {
              alt: localization.avatarAlt(content.DisplayName || content.Name),
              src: `${props.repository.configuration.repositoryUrl}${content.Avatar.Url}`
            }) : /*#__PURE__*/React.createElement(Avatar, null, ((_content$DisplayName = content.DisplayName) === null || _content$DisplayName === void 0 ? void 0 : _content$DisplayName[0]) || content.Name[0]) : undefined,
            icon: (_props$repository2 = props.repository) !== null && _props$repository2 !== void 0 && _props$repository2.schemas.isContentFromType(content, 'User') ? undefined : props.renderIcon ? props.renderIcon(content.Type.toLowerCase()) : renderIconDefault(content.Type.toLowerCase()),
            key: content.Id,
            label: content.DisplayName,
            onDelete: () => handleDelete(content.Id),
            onMouseDown: event => {
              event.stopPropagation();
            },
            className: classes.chip
          });
        })),
        MenuProps: menuProps
      }, dataSource === null || dataSource === void 0 ? void 0 : dataSource.map(content => /*#__PURE__*/React.createElement(MenuItem, {
        key: content.Id,
        value: content.Id
      }, content.DisplayName))), !props.hideDescription && /*#__PURE__*/React.createElement(FormHelperText, null, props.settings.Description));
    case 'browse':
    default:
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        gutterBottom: true
      }, props.settings.DisplayName), /*#__PURE__*/React.createElement(FormGroup, null, fieldValue && fieldValue.length > 0 ? fieldValue.map((content, index) => /*#__PURE__*/React.createElement(Typography, {
        variant: "body1",
        gutterBottom: index === fieldValue.length - 1,
        key: index
      }, content.DisplayName)) : /*#__PURE__*/React.createElement(Typography, {
        variant: "body1",
        gutterBottom: true
      }, localization.noValue)));
  }
};
//# sourceMappingURL=tags-input.js.map